/** @format */

import {Box, LinearProgress, Skeleton} from '@mui/material';
import {endOfDay, format, min} from 'date-fns';

import {Trans, useTranslation} from 'react-i18next';

import Alert from '@mui/material/Alert';

import {addFakeForecast} from 'pages/Home/DigitalCast/helpers/addFakeForecast';

import {useGetCurrentUserQuery} from 'graphql/operations/queries/userKpi/__generated__';

import {DTO_DATE_FORMAT} from 'pages/Home/DigitalCast/constants/common';

import {usePeriodSelector} from 'components/PeriodSelector/hooks/usePeriodSelector';

import WideLayout from 'layouts/WideLayout';

import {mapGraphStats} from 'pages/Home/DigitalCast/helpers/graph-stats/mapGraphStats';

import mapChartsData from 'pages/Home/DigitalCast/mapping/mapChartsData';

import getChartIdByKpiName from 'pages/Home/DigitalCast/helpers/getChartIdByKpiName';

import {StatState} from 'types';

import {getCurrentRechartsDates} from 'pages/Home/DigitalCast/helpers/getCurrentRechartsDates';

import {useDebugDate} from 'helpers/routerHelpers';

import {Title} from '../BudgetAndExpese/styles';
import Widget from './components/Widget';
import {TARGET_WIDGETS} from './constants';
import {computeEffectiveStartDate, computeTailDays, removePlanFromTail} from './helpers';
import {WidgetContainer, WidgetsGrid} from './styles';

interface Props {
  tokenStatus: StatState | null;
}

const KpiAndIndicators = ({tokenStatus}: Props) => {
  const debugDate = useDebugDate();
  const now = debugDate ?? new Date();

  const {startDate, endDate} = usePeriodSelector({
    defaultPeriod: 'current',
    now,
  });

  const effectiveEndDate = debugDate ? min([endDate, endOfDay(now)]) : endDate;

  const tailDays = computeTailDays(now);
  const effectiveStartDate = computeEffectiveStartDate(startDate, tailDays, now);

  const startDateString = format(effectiveStartDate, DTO_DATE_FORMAT);
  const endDateString = format(effectiveEndDate, DTO_DATE_FORMAT);

  const {t} = useTranslation();

  const {data, error, loading} = useGetCurrentUserQuery({
    variables: {
      startDate: startDateString,
      endDate: endDateString,
    },
  });

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }

  if (loading || !data?.currentUser?.currentCompany) {
    return (
      <WideLayout>
        <LinearProgress style={{flex: 1, marginTop: 16}} />
      </WideLayout>
    );
  }

  const mainKpi = data.currentUser.currentCompany.mediaplans[0]?.kpis?.find(
    ({primary}) => primary,
  )?.name;

  const targetWidgets = [...TARGET_WIDGETS];

  if (mainKpi) {
    const mainKpiConverted = getChartIdByKpiName(mainKpi);

    if (!targetWidgets.includes(mainKpiConverted)) {
      targetWidgets.unshift(mainKpiConverted);
    }
  }

  const rechartsDates = getCurrentRechartsDates({
    startDate: startDateString,
    endDate: endDateString,
    discreteness: 'day',
  });

  const {graphStats, graphComplexValues} = mapGraphStats({
    graphStats: data.currentUser.currentCompany.graphStats,
    mediaplanStats: data.currentUser.currentCompany.mediaplanStats,
    startDate: effectiveStartDate,
    endDate: effectiveEndDate,
    rechartsDates,
  });

  const {charts, widgetConditions, todayPointName} = mapChartsData({
    mediaplansDto: data.currentUser.currentCompany.mediaplans || [],
    graphStats,
    graphComplexValues,
    startDate: startDateString,
    endDate: endDateString,
    period: 'current',
    now,
    t,
    companyId: data.currentUser.currentCompany.id || '',
    analyticGoals: [],
    rechartsDates,
  });

  removePlanFromTail(charts.charts, tailDays);

  const chartsWithFakeForecast = addFakeForecast(charts);

  return (
    <Box>
      <Title>
        <Trans>KPI и показатели</Trans>
      </Title>
      {loading || !data ? (
        <Skeleton variant='rectangular' height={120} />
      ) : (
        <WidgetsGrid widgetsCount={targetWidgets.length}>
          {targetWidgets.map((chartId, index) => (
            <WidgetContainer key={chartId} widgetsCount={targetWidgets.length} index={index}>
              <Widget
                tokenStatus={tokenStatus}
                chartId={chartId}
                charts={chartsWithFakeForecast}
                widgetConditions={widgetConditions}
                graphComplexValues={graphComplexValues}
                todayPointName={todayPointName}
                tailDays={tailDays}
              />
            </WidgetContainer>
          ))}
        </WidgetsGrid>
      )}
    </Box>
  );
};

export default KpiAndIndicators;
